<template>
  <VApp>
    <VContainer class="fill-height" fluid>
      <VRow align="center" justify="center">
        <VCol cols="12" sm="8" md="6" lg="4">
          <VCard class="elevation-1">
            <VCardTitle class="text-h4 justify-center py-6">
              <VIcon :color="errorColor" size="64" class="mr-3">
                {{ errorIcon }}
              </VIcon>
              <span>{{ errorTitle }}</span>
            </VCardTitle>

            <VDivider />

            <VCardText class="text-center py-8">
              <div
                  class="text-h2 font-weight-bold mb-4"
                  :style="{ color: errorColor }"
              >
                {{ statusCode }}
              </div>

              <p class="text-h6 mb-4">
                {{ errorMessage }}
              </p>

              <p v-if="error?.message" class="text-body-2 text-grey">
                {{ error.message }}
              </p>
            </VCardText>

            <VDivider />

            <VCardActions class="justify-center pa-6" style="gap: 12px; flex-wrap: wrap;">
              <VBtn v-if="statusCode !== 403" color="primary" size="large" variant="elevated" @click="handleError">
                <VIcon start class="mr-2">mdi-home</VIcon>
                Retour à l'accueil
              </VBtn>
              <VBtn color="error" size="large" variant="elevated" @click="disconnect">
                <VIcon start class="mr-2">mdi-logout</VIcon>
                Se déconnecter
              </VBtn>
            </VCardActions>
          </VCard>
        </VCol>
      </VRow>
    </VContainer>
  </VApp>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { clearError } from '#app'

// Vuetify 3: imports officiels (pas de "vuetify/lib/..." qui casse le build)
import {
  VApp,
  VContainer,
  VRow,
  VCol,
  VCard,
  VCardTitle,
  VDivider,
  VCardText,
  VCardActions,
  VBtn,
  VIcon,
} from 'vuetify/components'

const props = defineProps<{
  error: {
    statusCode?: number
    message?: string
  }
}>()

const statusCode = computed(() => props.error?.statusCode ?? 500)

const errorConfig = computed(() => {
  const configs: Record<number, { title: string; message: string; icon: string; color: string }> = {
    404: {
      title: 'Page introuvable',
      message: "Désolé, la page que vous recherchez n'existe pas.",
      icon: 'mdi-file-question-outline',
      color: '#FF9800',
    },
    403: {
      title: 'Accès refusé',
      message: "Votre compte n'a pas le rôle nécessaire. Contactez un administrateur, puis déconnectez-vous.",
      icon: 'mdi-lock-alert-outline',
      color: '#F44336',
    },
    500: {
      title: 'Erreur serveur',
      message: "Une erreur s'est produite sur le serveur. Veuillez réessayer plus tard.",
      icon: 'mdi-server-network-off',
      color: '#F44336',
    },
  }

  return configs[statusCode.value] ?? {
    title: "Une erreur s'est produite",
    message: "Quelque chose s'est mal passé.",
    icon: 'mdi-alert-circle-outline',
    color: '#F44336',
  }
})

const errorTitle = computed(() => errorConfig.value.title)
const errorMessage = computed(() => errorConfig.value.message)
const errorIcon = computed(() => errorConfig.value.icon)
const errorColor = computed(() => errorConfig.value.color)

const handleError = () => {
  const { homePath } = useHomePath()
  clearError({ redirect: homePath() })
}

const disconnect = async () => {
  const { logout } = useAuth()
  logout()
  await clearError({ redirect: '/auth/login' })
}
</script>

<style scoped>
.fill-height {
  min-height: 100vh;
}
</style>
